import { parseApiError } from '@/lib/api-error';
import type { CardCaptureApi } from '@/components/billing/card-capture';
import type { BillingOutcome } from '@/lib/billing-card';

const API_URL = import.meta.env.VITE_API_URL ?? '';

const TOKEN_HEADER = 'X-Card-Link-Token';

export type CardLinkStatus = 'trialing' | 'paused' | 'active';

export interface CardLinkPlan {
  name: string;
  priceCents: number;
  interval: 'monthly' | 'annual';
}

export type CardLinkResolveResult =
  | { done: true; plansUrl: string }
  | {
      done: false;
      orgName: string;
      status: CardLinkStatus;
      trialEndsAt: string | null;
      plan: CardLinkPlan | null;
      phoneVerified: boolean;
    };

/**
 * GET /card-link/resolve — the token identifies the org; a bad/revoked token
 * comes back 404 and is surfaced as the same neutral "link is no longer
 * active" copy every other public onboarding surface uses (never leak
 * "invalid token" / "expired" distinctions).
 */
export async function resolveCardLink(
  token: string,
): Promise<{ ok: true; data: CardLinkResolveResult } | { ok: false; message: string }> {
  try {
    const res = await fetch(`${API_URL}/card-link/resolve`, {
      headers: { [TOKEN_HEADER]: token },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const apiError = parseApiError(res.status, body, 'This link is no longer active.');
      return { ok: false, message: apiError.userMessage };
    }
    return { ok: true, data: (await res.json()) as CardLinkResolveResult };
  } catch {
    return { ok: false, message: 'Connection error. Please try the link again.' };
  }
}

/**
 * POST /card-link/setup-intent. `done: true` surfaces a 409 CARD_LINK_DONE —
 * the flow completed elsewhere (another tab, a race with resolve) between
 * page load and mint; the caller treats that as "nothing left to do" rather
 * than an error.
 */
export async function mintCardLinkSetupIntent(
  token: string,
): Promise<
  | { ok: true; clientSecret: string; plan?: CardLinkPlan | null }
  | { ok: false; done: boolean; message: string }
> {
  try {
    const res = await fetch(`${API_URL}/card-link/setup-intent`, {
      method: 'POST',
      headers: { [TOKEN_HEADER]: token },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const apiError = parseApiError(res.status, body, 'Could not start adding a card. Please try again.');
      return { ok: false, done: res.status === 409, message: apiError.userMessage };
    }
    // AIT-505 P1 (Codex round 8) — the mint response is the authoritative
    // quote (`plan`, present for paused/past_due orgs). The page must render
    // from THIS plan, not the earlier `resolve()` quote, so a plan resolved
    // fresh at mint time (after an eligibility threshold crossed) is what
    // gets shown, matching what was just frozen onto the SetupIntent.
    const data = (await res.json()) as { clientSecret: string; plan?: CardLinkPlan | null };
    return { ok: true, clientSecret: data.clientSecret, plan: data.plan };
  } catch {
    // Review fix — an unhandled rejection here (network drop, DNS failure)
    // used to escape past the page's `void mint()` call and leave the
    // spinner stuck forever. Mirrors resolveCardLink's transport-error copy.
    return { ok: false, done: false, message: 'Connection error. Please try the link again.' };
  }
}

/**
 * The public-surface implementation of CardCaptureApi (shared with the
 * authenticated AddCardDialog — Task 7's card-capture.tsx). Every call
 * carries the card-link token instead of a WorkOS session; there is no
 * `listCards` here — a token has nowhere authenticated to list saved cards
 * from, and CardCapture already treats it as optional.
 */
export function buildCardLinkApi(token: string): CardCaptureApi {
  return {
    async persistCard(setupIntent) {
      const setupIntentId = setupIntent?.id;
      if (!setupIntentId) {
        return { ok: false, message: 'Could not read the saved card. Please try again.' };
      }
      let res: Response;
      try {
        res = await fetch(`${API_URL}/card-link/payment-method`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', [TOKEN_HEADER]: token },
          body: JSON.stringify({ setupIntentId }),
        });
      } catch {
        return {
          ok: false,
          message: 'Saved your card but could not reach the server to enable it. Please try again.',
        };
      }
      if (!res.ok) {
        const errBody = (await res.json().catch(() => null)) as { code?: string } | null;
        // Codex round 4 (P2) — CARD_LINK_DONE means the card already saved
        // (the first POST committed but its response was lost, e.g. a
        // network drop after the server wrote). Treat the retry as success
        // instead of leaving the customer stuck on a permanent error.
        if (errBody?.code === 'CARD_LINK_DONE') {
          return { ok: true, billingOutcome: 'saved' };
        }
        return { ok: false, message: 'Saved your card but could not enable it. Please try again.' };
      }
      const body = (await res.json().catch(() => null)) as { billingOutcome?: BillingOutcome } | null;
      return { ok: true, billingOutcome: body?.billingOutcome ?? 'saved' };
    },
    async sendOtp({ phone }) {
      const res = await fetch(`${API_URL}/card-link/phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [TOKEN_HEADER]: token },
        body: JSON.stringify({
          phone,
          // AIT-383: verifying is the consent signature for all categories —
          // the small print under the field states exactly that.
          consentOperational: true,
          consentProduct: true,
          consentMarketing: true,
          // No channel choice in this flow: the server fans a first
          // verification out over every channel and stores whichever one
          // delivered (AIT-511).
        }),
      });
      const body = (await res.json().catch(() => null)) as { delivery?: string; message?: string } | null;
      return { sent: res.ok && body?.delivery === 'sent', message: body?.message };
    },
    async verifyOtp(code) {
      const res = await fetch(`${API_URL}/card-link/phone/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [TOKEN_HEADER]: token },
        body: JSON.stringify({ code }),
      });
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      return { ok: res.ok, message: body?.message };
    },
  };
}
